import { useState, useEffect } from "react";
import {
  Container,
  SimpleGrid,
  Tabs,
  Box,
  Input,
} from "@mantine/core";
import { Loader } from "../../components/common/Loader";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { IconSearch } from "@tabler/icons-react";
import { ProductCard } from "../../components/common/ProductCard";
import { productService, Product } from "../../services/productService";
import {
  categoryService,
  Category,
} from "../../services/categoryService";
import { useSEO } from "../../hooks/useSEO";

type TabCategory = {
  id: string;
  name: string;
};

export const ProductsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<TabCategory[]>([]);
  const [activeTab, setActiveTab] = useState<string>(
    categoryParam || "all"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const { seo } = useSEO("products");

  useEffect(() => {
    const loadData = async () => {
      try {
        // 🔥 STEP 1: Load from cache (instant)
        const cachedProducts = sessionStorage.getItem("products");
        const cachedCategories = sessionStorage.getItem("product_categories");

        if (cachedProducts && cachedCategories) {
          setProducts(JSON.parse(cachedProducts));
          setCategories(JSON.parse(cachedCategories));
          setLoading(false);
        }

        // 🔥 STEP 2: Fetch fresh data in background
        const [productsData, categoriesData] = await Promise.all([
          productService.getAllProducts(),
          categoryService.getAllCategories(),
        ]);

        setProducts(productsData);

        const mappedCategories: TabCategory[] = categoriesData.map(
          (c: Category) => ({
            id: c.id,
            name: c.name,
          })
        );

        const finalCategories = [
          { id: "all", name: "All Products" },
          ...mappedCategories,
        ];

        setCategories(finalCategories);

        // 🔥 STEP 3: Save cache
        sessionStorage.setItem("products", JSON.stringify(productsData));
        sessionStorage.setItem("product_categories", JSON.stringify(finalCategories));

      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const filtered = products.filter((p) => {
    const matchCategory =
      activeTab === "all" || p.category?.id === activeTab;

    const matchSearch =
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.description || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    return matchCategory && matchSearch;
  });

  return (
    <>
      <Helmet>
        <title>{seo?.metaTitle}</title>
        <meta name="description" content={seo?.metaDescription} />
        <meta name="keywords" content={seo?.metaKeywords?.join(", ")} />
        <meta property="og:title" content={seo?.ogTitle} />
        <meta property="og:description" content={seo?.ogDescription} />
        {seo?.ogImage && <meta property="og:image" content={seo.ogImage} />}
        <meta name="twitter:title" content={seo?.twitterTitle} />
        <meta name="twitter:description" content={seo?.twitterDescription} />
        {seo?.twitterImage && <meta name="twitter:image" content={seo.twitterImage} />}
        {seo?.canonicalUrl && <link rel="canonical" href={seo.canonicalUrl} />}
      </Helmet>

      <Container size="xl">
        {/* 🔥 Search always instant */}
        <Box mb={20}>
          <Input
            placeholder="Search..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
          />
        </Box>

        {/* 🔥 Tabs instant (from cache or empty first render) */}
        <Tabs value={activeTab} onChange={(v) => setActiveTab(v || "all")}>
          <Tabs.List>
            {categories.map((c) => (
              <Tabs.Tab key={c.id} value={c.id}>
                {c.name}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>

        {/* 🔥 Non-blocking loader */}
        {loading && (
          <div style={{  marginTop: 20 }}>
            <Loader />
          </div>
        )}

        {/* 🔥 Products grid */}
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }} mt="lg">
          {filtered.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </SimpleGrid>

        {filtered.length === 0 && !loading && (
          <Box mt="lg" style={{ textAlign: "center" }}>
            No products found
          </Box>
        )}
      </Container>
    </>
  );
};